/**
 * 飞书错误上报 - 上报核心逻辑
 */

import type { ErrorInfo, FeishuErrorReporterConfig } from './types';
import { formatFeishuMessage } from './formatters';
import { reAddErrorToQueue } from './queue';

/**
 * 获取当前环境对应的飞书webhook地址
 */
export function getFeishuWebhookUrl(config: FeishuErrorReporterConfig): string | null {
  const env = config.environment;
  const url = config.webhookUrls[env] || config.webhookUrls.production;
  return url || null;
}

/**
 * 检查是否启用错误上报
 */
export function isEnabled(config: FeishuErrorReporterConfig): boolean {
  if (typeof config.enabled === 'function') {
    return config.enabled(config.environment);
  }

  if (typeof config.enabled === 'boolean') {
    return config.enabled;
  }

  // 默认：production环境启用
  return config.environment === 'production';
}

/**
 * 上报错误到飞书
 */
export async function reportErrorToFeishu(error: ErrorInfo, config: FeishuErrorReporterConfig): Promise<void> {
  if (!isEnabled(config)) {
    return;
  }

  try {
    const webhookUrl = getFeishuWebhookUrl(config);
    if (!webhookUrl) {
      console.warn('飞书webhook地址未配置');
      return;
    }

    // 检查是否需要忽略此错误
    const ignoredErrors = config.ignoredErrors || [];
    if (ignoredErrors.some((msg) => error.message.includes(msg))) {
      return;
    }

    const messageData = formatFeishuMessage(error, config);

    // 使用自定义请求函数或默认fetch
    if (config.requestFn) {
      await config.requestFn(webhookUrl, messageData);
    } else {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    console.log('错误上报成功:', error.message);
  } catch (reportError) {
    console.error('错误上报失败:', reportError instanceof Error ? reportError.message : String(reportError));
    // 上报失败时，将错误重新加入队列（避免无限循环）
    reAddErrorToQueue(error);
  }
}
